import { useEffect, useRef, useState, useContext } from "react";
import StoreContext from "@/context/store";
import { useShoppingCart } from "@/context/cart";
import { getTodayDeliveryHours } from "@/utils/storeHours";
import { extractAddress } from "@/utils/exactAddress";
import { getDistanceInMiles } from "@/utils/distance";
import { loadGoogleMapsScript } from "@/lib/googleMaps";
import Button from "../Button";
import SkeletonElement from "../skeletons/SkeletonElement";
import styles from "./DeliveryAddressForm.module.scss";
import LocationCrosshairs from "/packages/icons/location-crosshairs.svg";
import Xmark from "/packages/icons/xmark.svg";
import Phone from "/packages/icons/phone.svg";
import Clock from "/packages/icons/clock.svg";
import LocationDot from "/packages/icons/location-dot.svg";

const apiKey = process.env.NEXT_PUBLIC_GOOGLE_API;

// todo / issue - address input is synced to each component's state, causing a mismatch between duplicate components (e.g. modal, age gate, and page). Lift this to store context...? Also, the age gate "selectedDeliveryStore" and "...address" could also be lifted.

// todo - use /roots/store_operations_api/v1/stores/{id}/delivery_info instead of store data to extract delivery method. This will be required for utilizing Jane's Zipcode, GeoJSON, and Radius delivery setup. 

export default function DeliveryAddressForm({
  button = true, // show "shop delivery/continue shopping" button or not
  storeSuccessMessage = false, // show store details or not
  modal, // for nav location modal
  setModal, // for nav location modal
  setSelectedDeliveryStore, // for age gate
  setSelectedDeliveryAddress, // for age gate
  setWarning, // for age gate
}) {
  const inputRef = useRef(null);
  const formRef = useRef(null);
  const [inputValue, setInputValue] = useState("");
  const [message, setMessage] = useState(null);
  const [storeDetails, setStoreDetails] = useState(null);
  const [storeDetailsTimeout, setStoreDetailsTimeout] = useState(false);

  const {
    stores,
    deliveryZips,
    fetchStores,
    fetchDeliveryZips,
    setStore,
    setZipcode,
    setAddress,
    setStoreName,
    setOrderType,
    orderType,
    address,
    store,
  } = useContext(StoreContext);

  const { setCartItems } = useShoppingCart();

  // 🏪 Fetch available stores and delivery ZIPs on component mount. Perhaps pass these instead if available in context/cache?
  useEffect(() => {
    fetchStores();
    fetchDeliveryZips();
  }, []);

  // 🔁 Clear form if user switches to Pickup
  useEffect(() => {
    if (orderType === "Pickup") {
      clearForm();
    }
  }, [orderType]);

  // 📍 Load Google Maps Autocomplete and attach listener to input
  useEffect(() => {
    loadGoogleMapsScript().then(() => {
      const autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          fields: ["formatted_address", "address_components", "geometry"],
        }
      );

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place || !Array.isArray(place.address_components)) {
          return setMessage({
            type: "error",
            text: "❌ Could not retrieve your location. Please try again.",
          });
        }

        const extracted = extractAddress(place);
        setInputValue(extracted.plain());
        handleAddressSelection(extracted);
      });
    });
  }, []);

  // ⏳ If store details don't load within 8 seconds, show a fallback warning
  useEffect(() => {
    if (message?.type === "success" && !storeDetails) {
      const timeout = setTimeout(() => {
        setStoreDetailsTimeout(true);
      }, 8000);

      return () => clearTimeout(timeout);
    }
  }, [message, storeDetails]);

  // 📬 Main handler for processing and validating selected address
  const handleAddressSelection = async (extracted) => {
    if (!Array.isArray(deliveryZips) || !Array.isArray(stores)) {
      return setMessage({
        type: "error",
        text: "❌ Store data not available yet. Please try again shortly.",
      });
    }

    if (!extracted.zip) {
      return setMessage({
        type: "error",
        text: "⚠️ Could not detect a ZIP code.",
      });
    }

    const matchedZip = deliveryZips.find((z) => z.zipcode === extracted.zip);
    if (!matchedZip) {
      return setMessage({
        type: "error",
        text: `❌ We don't deliver to ZIP code ${extracted.zip}.`,
      });
    }

    const matchedStore = stores.find((s) => s.id === matchedZip.store_id);
    if (!matchedStore) {
      return setMessage({
        type: "error",
        text: "❌ Store not found for your area.",
      });
    }

    if (store !== matchedStore.id) setCartItems([]); // 🧺 Reset cart if switching store

    // 🧠 Save all state related to delivery setup
    setOrderType("Delivery");
    setStore(matchedStore.id);
    setStoreName(
      `${extracted.street()}${extracted.city ? `, ${extracted.city}` : ""}`
    );
    setZipcode(extracted.zip);
    setAddress(extracted);
    setMessage({ type: "success" });

    // 👶 For AgeGate or modal consumers
    setSelectedDeliveryStore?.(matchedStore);
    setSelectedDeliveryAddress?.(extracted.plain());
    setWarning?.(false);

    try {
      const res = await fetch(`/api/jane-store/${matchedStore.id}`);
      const data = await res.json();
      setStoreDetails(data.data.store); // 🏪 Load store info for UI
    } catch (err) {
      console.error("Failed to load store details:", err);
    }
  };

  // 📡 Geolocation + reverse geocode fallback option
  const handleUseCurrentLocation = async (e) => {
    e.preventDefault();
    formRef.current?.reset();
    setInputValue("");
    setMessage(null);

    if (!navigator.geolocation) {
      return setMessage({ type: "error", text: "Geolocation not supported." });
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`;

        try {
          const res = await fetch(geocodeUrl);
          const data = await res.json();
          const place = data.results?.[0];
          if (!place) throw new Error("No results");

          const fakePlace = {
            address_components: place.address_components,
            geometry: {
              location: { lat: () => latitude, lng: () => longitude },
            },
          };

          const extracted = extractAddress(fakePlace);
          setInputValue(extracted.plain());
          handleAddressSelection(extracted);
        } catch (err) {
          console.error(err);
          setMessage({
            type: "error",
            text: "❌ Could not retrieve your location.",
          });
        }
      },
      () =>
        setMessage({ type: "error", text: "❌ Location permission denied." })
    );
  };

  // 🧼 Utility to reset the form and related state
  const clearForm = () => {
    formRef.current?.reset();
    setInputValue("");
    setAddress(null);
    setZipcode("");
    // setStore(null);
    setStoreDetails(null);
    setStoreDetailsTimeout(false);
    setMessage(null);
  };

  return (
    <div className={styles.location}>
      <div className={styles.findMyLocation} onClick={handleUseCurrentLocation}>
        <LocationCrosshairs />
        Use Current Location
      </div>

      <form
        ref={formRef}
        className={styles.search}
        onSubmit={(e) => e.preventDefault()}
      >
        <div className={styles.inputWrapper}>
          <label htmlFor="address" />
          <span className="visually-hidden">Enter your delivery address</span>
          <input
            ref={inputRef}
            id="address"
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Enter your delivery address"
            autoComplete="off"
          />
          {inputValue && (
            <button
              type="button"
              id="ignoreUseOnClickOutside"
              onClick={clearForm}
              className={styles.clearInput}
              aria-label="Clear address input"
            >
              <Xmark />
            </button>
          )}
        </div>
      </form>

      {message?.type === "success" && address && (
        <div>
          <div className={styles.successMessage}>
            ✔️ We deliver to {address.street_number} {address.route},{" "}
            {address.city}
          </div>

          {storeSuccessMessage && !storeDetails && !storeDetailsTimeout && (
            <div className="flex center">
              <div className={styles.storeDetails}>
                <div className="flex align-center space-between gap wrap mb-25">
                  <SkeletonElement type="tag" />
                  <SkeletonElement type="tag" />
                </div>
                <SkeletonElement type="title" className="mt-25 mb-50" />
                <SkeletonElement type="textSmall" />
                <SkeletonElement type="textSmall" />
                <SkeletonElement type="textSmall" />
              </div>
            </div>
          )}

          {storeSuccessMessage && storeDetailsTimeout && (
            <div className={styles.errorMessage}>
              ⚠️ We&apos;re having trouble loading your store details. Please try again.
            </div>
          )}

          {storeSuccessMessage && storeDetails && (
            <div className="flex center">
              <div className={styles.storeDetails}>
                <div className="flex align-center space-between gap wrap mb-25">
                  <div className={styles.yourStore}>Your store</div>
                  {address.lat && storeDetails.lat && (
                    <div className={styles.distance}>
                      {getDistanceInMiles(
                        address.lat,
                        address.lng,
                        storeDetails.lat,
                        storeDetails.long
                      )}
                      mi
                    </div>
                  )}
                </div>

                <div className="fw-600 balance">{storeDetails.name}</div>
                <div className="flex align-center gap link link--underline balance">
                  <LocationDot />
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&origin=${address.lat},${address.lng}&destination=${storeDetails.lat},${storeDetails.long}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {storeDetails.address}, {storeDetails.metro_area}
                  </a>
                </div>

                {storeDetails.phone && (
                  <div className="flex align-center gap link link--underline">
                    <Phone />
                    <a href={`tel:${storeDetails.phone.replace(/\D/g, "")}`}>
                      {storeDetails.phone}
                    </a>
                  </div>
                )}

                <div className="flex align-center gap">
                  <Clock />
                  <span>{getTodayDeliveryHours(storeDetails)}</span>
                </div>
              </div>
            </div>
          )}

          <div className={styles.button}>
            {button &&
              (modal ? (
                <Button
                  type="button"
                  onClick={() => setModal(false)}
                  className="primary mt-25 w-100"
                >
                  Continue Shopping
                </Button>
              ) : (
                <Button
                  type="link"
                  href="/shop"
                  className="primary mt-25 w-100"
                >
                  Shop Delivery
                </Button>
              ))}
          </div>
        </div>
      )}

      {message?.type === "error" && (
        <p className={styles.errorMessage}>{message.text}</p>
      )}
    </div>
  );
}
